import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminStore } from '../../store/adminStore'
import Spinner from '../../components/common/Spinner'
import {
  Avatar,
  Badge,
  EmptyState,
  PillGroup,
  SearchInput,
  fmtRelative,
  styles,
  tokens,
} from './_ui'

type VerifiedFilter = '' | 'yes' | 'no'

export default function AdminShopsPage() {
  const navigate = useNavigate()
  const {
    crowdMerchants,
    totalCrowdMerchants,
    fetchCrowdMerchants,
    isLoading,
    error,
  } = useAdminStore()

  const [search, setSearch] = useState('')
  const [searchDebounce, setSearchDebounce] = useState('')
  const [verifiedFilter, setVerifiedFilter] = useState<VerifiedFilter>('')
  const isFirstLoad = useRef(true)

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounce(search), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    const params: Parameters<typeof fetchCrowdMerchants>[0] = {
      skip: 0,
      search: searchDebounce || undefined,
    }
    if (verifiedFilter === 'yes') params.verified = true
    else if (verifiedFilter === 'no') params.verified = false

    fetchCrowdMerchants(params).finally(() => {
      isFirstLoad.current = false
    })
  }, [searchDebounce, verifiedFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasMore = crowdMerchants.length < totalCrowdMerchants
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasMore || isLoading) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          const params: Parameters<typeof fetchCrowdMerchants>[0] = {
            skip: crowdMerchants.length,
            search: searchDebounce || undefined,
          }
          if (verifiedFilter === 'yes') params.verified = true
          else if (verifiedFilter === 'no') params.verified = false
          fetchCrowdMerchants(params, true)
        }
      },
      { rootMargin: '300px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, isLoading, crowdMerchants.length, searchDebounce, verifiedFilter, fetchCrowdMerchants])

  if (isLoading && isFirstLoad.current) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div style={{ padding: '8px 16px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Hero KPI */}
      <div
        style={{
          background: 'linear-gradient(135deg, #6366f120, #ec489920)',
          borderRadius: 18,
          padding: 18,
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 32, fontWeight: 800, color: tokens.text, letterSpacing: '-0.02em' }}>
          {totalCrowdMerchants}
        </span>
        <span style={{ ...styles.subtle, fontSize: 13 }}>shops on the platform</span>
      </div>

      <SearchInput value={search} onChange={setSearch} placeholder="Search shops…" />

      <PillGroup<VerifiedFilter>
        value={verifiedFilter}
        onChange={setVerifiedFilter}
        options={[
          { value: '', label: 'All' },
          { value: 'yes', label: '✓ Verified' },
          { value: 'no', label: 'Unverified' },
        ]}
      />

      {error && (
        <p style={{ fontSize: 13, textAlign: 'center', color: tokens.red, padding: '8px 0' }}>
          {error}
        </p>
      )}

      {!isLoading && crowdMerchants.length === 0 && !error && (
        <EmptyState emoji="🏪" title="No shops found" subtitle="Try adjusting your filters" />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {crowdMerchants.map((m) => (
          <div
            key={m.id}
            onClick={() => navigate(`/admin/shops/${m.id}`)}
            style={{
              background: tokens.cardBg,
              borderRadius: 14,
              padding: 14,
              cursor: 'pointer',
              transition: 'transform 0.1s',
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.99)')}
            onMouseUp={(e) => (e.currentTarget.style.transform = '')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = '')}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <Avatar name={m.business_name} emoji="🏪" size={44} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <p
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: tokens.text,
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {m.business_name}
                  </p>
                  {m.is_crowd_verified && <Badge tone="green">✓</Badge>}
                </div>
                {m.address && (
                  <p
                    style={{
                      fontSize: 12,
                      opacity: 0.5,
                      color: tokens.text,
                      margin: '2px 0 0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    📍 {m.address}
                  </p>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginTop: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <Badge>📦 {m.product_count}</Badge>
                  {(m.member_count ?? 0) > 0 && <Badge tone="purple">👥 {m.member_count}</Badge>}
                  {m.owner_username && (
                    <span style={{ fontSize: 11, opacity: 0.5, color: tokens.text, fontFamily: 'monospace' }}>
                      @{m.owner_username}
                    </span>
                  )}
                  {m.shop_category && (
                    <span style={{ fontSize: 11, opacity: 0.45, color: tokens.text }}>
                      {m.shop_category}
                    </span>
                  )}
                  {m.created_at && (
                    <span style={{ fontSize: 11, opacity: 0.4, color: tokens.text, marginLeft: 'auto' }}>
                      {fmtRelative(m.created_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>

          </div>
        ))}
      </div>

      <div ref={sentinelRef} style={{ height: 1 }} />
      {hasMore && isLoading && (
        <div style={{ ...styles.subtle, fontSize: 12, textAlign: 'center', padding: '8px 0' }}>
          Loading more…
        </div>
      )}
      {!hasMore && crowdMerchants.length > 0 && (
        <div style={{ ...styles.faint, fontSize: 11, textAlign: 'center', padding: '8px 0' }}>
          End of list · {totalCrowdMerchants} shops
        </div>
      )}
    </div>
  )
}
